// THE CONDITION STEP'S "PHOTOS ARE YOUR STRONGEST EVIDENCE" BLOCK (R-pending-web-photo-block).
//
// Tyler, 2026-10-22, verbatim: "3 yes" — to the draft in ~/dev/proppeal-handoff/specs/approved-wording-2026-10-22.md, section
// "Condition step — photos", as written. Why it is worth a block: Douglas County's 2026 referee decisions show condition
// photos and owner testimony won reductions (the spec notes, "Why the board lowered four homes"). These are the WEBSITE's
// words; the iPhone app gets the same block in its next build (PropertyConditionView.swift, above the "Photo evidence" card
// that CaseEvidenceCards.swift draws).
//
// EVERY PIECE BELOW IS THE APPROVED TEXT, WORD FOR WORD, AND NOTHING ELSE IS SAID. The three examples are Douglas County
// (Nebraska) only, so the block has two forms:
//   - DOUGLAS COUNTY, NE: the heading, the intro (its first sentence and the sentence that leads into the three examples),
//     the three examples, "What to photograph" and the tip;
//   - EVERY OTHER COUNTY: only the pieces that are not Douglas-specific — the heading, the intro's first sentence, "What to
//     photograph" and the tip. The sentence that names Douglas County and the three examples are not said. No sentence was
//     written for it: it is the approved block with the Douglas pieces left out, which Tyler asked for in the brief
//     ~/dev/proppeal-handoff/briefs/web-photo-block.md.
// The block names no address and no owner, and nothing in it is filled from the home or the appeal.
//
// WHICH FORM IS NOT THIS FILE'S WORDS. It is decided from the two facts the step already holds — the subject's state and county
// on the appeal snapshot its own call returns (`isDouglasCountyNebraska`). A county's name is not enough (Douglas County is
// also in Colorado, Georgia, Kansas, Minnesota, Missouri, Nevada, Oregon, South Dakota, Washington, Wisconsin and Illinois),
// so the state must be Nebraska too, and a missing state or county is the other counties' form: silence never says
// "Douglas".
//
// IF A PIN FAILS, DO NOT EDIT THE PIN. A changed word is a new sentence: it goes back to Tyler.

/** What Tyler said, and when. One reply approved the whole block. */
export const PHOTO_BLOCK_APPROVAL = {
  by: "Tyler",
  on: "2026-10-22",
  quote: "3 yes",
  source: "~/dev/proppeal-handoff/specs/approved-wording-2026-10-22.md, section \"Condition step — photos\"",
} as const;

export interface ApprovedPhotoPiece {
  readonly text: string;
  readonly status: "approved";
  /** Tyler's reply. */
  readonly approval: typeof PHOTO_BLOCK_APPROVAL;
  /** Whether the sentence names Douglas County, or states its three examples: said only for Douglas County, Nebraska. */
  readonly douglasOnly: boolean;
}

function approved(text: string, douglasOnly: boolean): ApprovedPhotoPiece {
  return { text, status: "approved", approval: PHOTO_BLOCK_APPROVAL, douglasOnly };
}

export const PHOTO_BLOCK_WORDING = {
  "photoBlock.heading": approved("Photos are your strongest evidence", false),
  // The intro's first sentence, said everywhere.
  "photoBlock.lead": approved("Public records can't show what's wrong with your home.", false),
  // The intro's second sentence, which leads into the three examples: Douglas County, Nebraska only.
  "photoBlock.examplesLead": approved("In the 2026 Douglas County protests we reviewed, boards lowered values when owners showed it:", true),
  "photoBlock.example.1": approved("Photos of a cracked foundation and water damage: value lowered 15%.", true),
  "photoBlock.example.2": approved("Photos of repairs needed throughout a rental home: lowered 7%.", true),
  "photoBlock.example.3": approved("A bathroom and second floor that need updating, and a basement that's only a rec room: lowered 14%.", true),
  // "What to photograph:" is in bold in the approved block, then the list.
  "photoBlock.whatLabel": approved("What to photograph:", false),
  "photoBlock.whatList": approved(
    "cracks in the foundation or walls · water stains, leaks or a damp basement · roof, siding or window damage · a dated or worn kitchen or bathroom · a basement that's only partly or basically finished · anything a buyer would ask a discount for.",
    false,
  ),
  // "Tip:" is in bold in the approved block, then the sentences.
  "photoBlock.tipLabel": approved("Tip:", false),
  "photoBlock.tip": approved(
    "take one wide photo and one close-up of each problem. Add a short caption: what it is, where it is, and the repair cost if you have a quote.",
    false,
  ),
} as const satisfies Record<string, ApprovedPhotoPiece>;

export type PhotoBlockWordingId = keyof typeof PHOTO_BLOCK_WORDING;

export function photoWords(id: PhotoBlockWordingId): string {
  return PHOTO_BLOCK_WORDING[id].text;
}

/** The examples, in the approved order. */
export const PHOTO_BLOCK_EXAMPLES = ["photoBlock.example.1", "photoBlock.example.2", "photoBlock.example.3"] as const satisfies readonly PhotoBlockWordingId[];

/** "Douglas County" as the server's `normalizeCounty` (functions/src/compJurisdiction.ts) reads a county: trimmed, one space
 *  between words, upper case, the jurisdiction word after the name removed. */
function countyKey(raw: string | null | undefined): string | null {
  if (typeof raw !== "string") return null;
  const v = raw.trim().replace(/\s+/g, " ").toUpperCase().replace(/\s+COUNTY$/, "").trim();
  return v === "" ? null : v;
}

/** Whether this home is in Douglas County, Nebraska, from the subject's own state and county. Both must be there and say so;
 *  anything else — a missing figure, Douglas County in another state, another county — is `false`, the form with no
 *  Douglas-specific sentence. The state is the two letters the server holds (the Details step upper-cases an owner's
 *  correction), so "Nebraska" spelled out is not read as Nebraska: a miss shows the smaller block, never the larger one. */
export function isDouglasCountyNebraska(state: string | null | undefined, county: string | null | undefined): boolean {
  if (typeof state !== "string" || state.trim().toUpperCase() !== "NE") return false;
  return countyKey(county) === "DOUGLAS";
}
